import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import LoadSpinner from "../../../utilities/LoadSpinner";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { API_get_learning_section_detail } from "../../../constants";
import { getRightworkAPI } from "../requests/utilities/requests";
import BadgeEarnedModal from "./BadgeEarnedModal";
import "@blocknote/core/fonts/inter.css";
import "@blocknote/mantine/style.css";
import { buildTrainingSchema } from "../create_training_content/trainingBlockSpecs";
import SectionPreview from "../create_training_content/SectionPreview";
import { Quiz } from "../create_training_content/Quiz";
import { Checkpoint } from "../create_training_content/Checkpoint";
import { PDF } from "../create_training_content/PDF";
import { Signature } from "../create_training_content/Signature";
import { DocumentUpload } from "../create_training_content/DocumentUpload";
import { ManagerInput } from "../create_training_content/ManagerInput";
import { SwipeDeck } from "../create_training_content/SwipeDeck";
import { VideoEmbed } from "../create_training_content/VideoEmbed";
import { Form } from "../create_training_content/Form";
import { Roleplay } from "../create_training_content/Roleplay";
import TrackSidebarModuleDropdown from "../create_training_content/TrackSidebarModuleDropdown";
import { findNextSectionPosition } from "../create_training_content/testOutRules";
import { formatName } from "../../../helpers/helpers";

function CompleteTrack(props) {
  const t = (english, spanish, chinese) => {
    if (props.language === "chinese") return chinese;
    if (props.language === "spanish") return spanish;
    return english;
  };

  const moduleCountLabel = (count) => {
    if (props.language === "chinese") return `${count} 个模块`;
    if (props.language === "spanish") return `${count} ${count === 1 ? "módulo" : "módulos"}`;
    return `${count} ${count === 1 ? "module" : "modules"}`;
  };

  const [showMenu, setShowMenu] = React.useState(true);

  const [isLoading, setIsLoading] = React.useState(false);

  const [selectedModuleIndex, setSelectedModuleIndex] = React.useState(
    props.currentTrack ? props.currentTrack.next_module_index : 0
  );

  const [selectedSectionIndex, setSelectedSectionIndex] = React.useState(
    props.currentTrack ? props.currentTrack.next_section_index : null
  );

  const modules = props.modalDataTrack?.modules;

  const selectedSectionFromTrack =
    modules?.[selectedModuleIndex]?.sections?.[selectedSectionIndex];

  // Completion of the section currently in view, read straight from the
  // per-section enrollment annotation (driven by lse.completed_at, which the
  // completion endpoint writes synchronously). We gate forward navigation on
  // THIS rather than on currentTrack.next_section_index: that frontier index is
  // recomputed by an async Celery task, so right after a completion it can be
  // stale, and the old `next_section_index === selectedSectionIndex` equality
  // check would then never match and leave "Next" enabled all the way past a
  // checkpoint/quiz block. Reading the annotation also avoids the one-render lag
  // of the isCurrentSectionCompleted state when stepping between sections.
  const currentSectionIsCompleted = Boolean(
    modules?.[selectedModuleIndex]?.sections?.[selectedSectionIndex]
      ?.annotated_enrollment?.is_completed
  );

  // An employee may only advance once the section they're on is complete (an
  // employee cannot complete a blocked section — the backend's can_complete
  // enforces quizzes/checkpoints/etc. — so "completed" reliably means
  // "cleared to move on"). Managers navigate freely.
  const employeeBlockedFromAdvancing =
    props.userType === "Employee" && !currentSectionIsCompleted;

  const initialNextSectionUUIDRef = React.useRef(
    props.currentTrack?.next_section?.uuid
  );
  const initialSectionUUID =
    initialNextSectionUUIDRef.current || selectedSectionFromTrack?.uuid;
  const currentSelectionSectionUUID =
    selectedSectionFromTrack?.uuid || initialNextSectionUUIDRef.current;

  const [selectedSectionUUID, setSelectedSectionUUID] = React.useState(
    initialSectionUUID
  );
  const selectedSectionTitle =
    props.modalDataSection?.uuid === selectedSectionUUID
      ? props.modalDataSection?.name || selectedSectionFromTrack?.name
      : selectedSectionFromTrack?.name;

  // Use a ref to ensure the latest sectionUUID is always available to BlockNote render functions
  // Update the ref synchronously during render (before child components read it)
  const selectedSectionUUIDRef = React.useRef(selectedSectionUUID);
  selectedSectionUUIDRef.current = selectedSectionUUID;

  const [refreshSection, setRefreshSection] = React.useState(false);
  // Scroll position to restore after an in-place refresh (form/quiz/checkpoint
  // submit). The refresh remounts <SectionPreview>, which rebuilds every block
  // and collapses scroll to 0 — capture before, restore after, so the learner
  // stays where they were instead of being thrown to the top of the section.
  const preservedScrollRef = React.useRef(null);

  React.useEffect(() => {
    if (refreshSection) {
      // Captured here (before the fetch) while the content is still at the
      // learner's scroll position.
      preservedScrollRef.current = contentRef.current
        ? contentRef.current.scrollTop
        : null;
      // showLoading = FALSE: refresh in place. The full-screen loading state
      // unmounts <SectionPreview> and swaps in a spinner, which flashes and
      // rebuilds every block (collapsing scroll). An in-place refresh keeps the
      // editor mounted and just re-seeds its blocks, so a form/quiz/checkpoint
      // submit updates smoothly without a flash. Navigation still passes true.
      fetchSectionDetail(
        false,
        selectedSectionUUID,
        props.selectedEmployee ? props.selectedEmployee?.id : null
      );
    }
    setRefreshSection(false);
  }, [refreshSection]);

  // console.log("SELECTED SECTION UUID", selectedSectionUUID)

  // console.log("MODULEINDEX", selectedModuleIndex)
  // console.log("SECTIONINDEX", selectedSectionIndex)

  // Checking small screen to allow closing menu when clicking / pressing outside of the menu
  const [isSmallScreen, setIsSmallScreen] = React.useState(false);

  React.useEffect(() => {
    // Function to check if the screen is small
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth <= 640); // Adjust the breakpoint as needed
    };

    // Check screen size on initial render
    checkScreenSize();

    // Add event listener to handle window resize
    window.addEventListener("resize", checkScreenSize);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, []);

  React.useEffect(() => {
    if (!currentSelectionSectionUUID) return;
    if (selectedSectionUUID === currentSelectionSectionUUID) return;
    setSelectedSectionUUID(currentSelectionSectionUUID);
  }, [currentSelectionSectionUUID, selectedSectionUUID]);

  const [isCurrentSectionCompleted, setIsCurrentSectionCompleted] =
    React.useState(
      modules?.[selectedModuleIndex]?.sections?.[selectedSectionIndex]
        ?.annotated_enrollment?.is_completed
    );

  React.useEffect(() => {
    if (selectedModuleIndex !== null && selectedSectionIndex !== null) {
      setIsCurrentSectionCompleted(
        modules?.[selectedModuleIndex]?.sections?.[selectedSectionIndex]
          ?.annotated_enrollment?.is_completed
      );
    }
    // setSelectedSectionUUID(props.modalDataTrack?.modules?.[selectedModuleIndex]?.sections?.[selectedSectionIndex]?.uuid)
  }, [selectedModuleIndex, selectedSectionIndex, modules]);

  const lastSectionInModule =
    modules?.[selectedModuleIndex]?.sections?.length ===
    selectedSectionIndex + 1;
  const lastSectionInTrack =
    modules?.length === selectedModuleIndex + 1 &&
    props.modalDataTrack?.modules?.[selectedModuleIndex]?.sections?.length ===
      selectedSectionIndex + 1;

  const firstSectionInTrack =
    selectedModuleIndex === 0 &&
    (selectedSectionIndex === null || selectedSectionIndex === 0);
  const firstSectionInModule =
    selectedModuleIndex !== 0 &&
    (selectedSectionIndex === null || selectedSectionIndex === 0);

  const percentCompleted = (
    (props.modalDataTrack?.annotated_enrollment?.sections_completed_count /
      props.modalDataTrack?.annotated_enrollment?.total_sections_count) *
    100
  ).toFixed();

  // Badge celebration: fires when THIS session's work flips the enrollment
  // to complete on a track that awards a badge. Keyed on the
  // completed-count transition (parent refetches after each section
  // completion), so opening an already-finished track never re-celebrates.
  // Fired here rather than when the EarnedBadge row lands because awarding
  // runs async in the enrollment-progress Celery task.
  //
  // The ref carries the track uuid alongside the count: the player can stay
  // mounted across track switches (both open flows render CompleteTrack
  // immediately while the new track's detail is still in flight, so
  // modalDataTrack briefly holds the PREVIOUS track). Comparing counts
  // across two different tracks would fire the celebration just for opening
  // an already-completed track after viewing an incomplete one - a uuid
  // change seeds the ref fresh instead of evaluating a transition.
  const [certEarnedOpen, setCertEarnedOpen] = React.useState(false);
  const prevCompletedCountRef = React.useRef(null); // { uuid, count }
  React.useEffect(() => {
    const trackUuid = props.modalDataTrack?.uuid;
    const completed =
      props.modalDataTrack?.annotated_enrollment?.sections_completed_count;
    const total = props.modalDataTrack?.annotated_enrollment?.total_sections_count;
    if (
      !trackUuid ||
      typeof completed !== "number" ||
      typeof total !== "number" ||
      total === 0
    ) {
      return;
    }
    const prevEntry = prevCompletedCountRef.current;
    prevCompletedCountRef.current = { uuid: trackUuid, count: completed };
    const prev =
      prevEntry && prevEntry.uuid === trackUuid ? prevEntry.count : null;
    if (
      prev !== null &&
      prev < total &&
      completed >= total &&
      props.modalDataTrack?.grants_badge_name
    ) {
      setCertEarnedOpen(true);
    }
  }, [
    props.modalDataTrack?.uuid,
    props.modalDataTrack?.annotated_enrollment?.sections_completed_count,
    props.modalDataTrack?.annotated_enrollment?.total_sections_count,
    props.modalDataTrack?.grants_badge_name,
  ]);

  const contentRef = React.useRef(null);
  const latestSectionDetailRequestUUIDRef = React.useRef(null);

  // Add this useEffect to scroll to top when section changes
  React.useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [selectedModuleIndex, selectedSectionIndex]);

  // // console.log("First Section In Track", firstSectionInTrack)
  // // console.log("First Section In Module", firstSectionInModule)
  // // console.log("Selected Section Index", selectedSectionIndex)
  // // console.log("Selected Module Index", selectedModuleIndex)

  // [GET] api/lms_info/get_learning_section_detail
  // * company_id
  // * learning_section_uuid
  const fetchSectionDetail = async (
    showLoading,
    learning_section_uuid,
    store_team_member_id,
  ) => {
    if (!learning_section_uuid) return false;
    latestSectionDetailRequestUUIDRef.current = learning_section_uuid;
    setIsLoading(showLoading);
    let api_params = {
      company_id: props.homeCompany,
      learning_section_uuid: learning_section_uuid,
      store_team_member_id: store_team_member_id,
      language: props.language || "english"
    };
    let api = API_get_learning_section_detail;
    try {
      const res = await getRightworkAPI(api, api_params);
      const learningSection = res.data.redux?.payload?.learning_section;
      if (latestSectionDetailRequestUUIDRef.current !== learning_section_uuid) {
        return false;
      }

      // WORKAROUND: Blocknote and signed URLs
      let mapSignedUrlToFileUuid =
        localStorage.getItem("mapSignedUrlToFileUuid") || "{}";
      mapSignedUrlToFileUuid = JSON.parse(mapSignedUrlToFileUuid);
      const rawBlocksAsJson = learningSection.as_json;
      rawBlocksAsJson.forEach((block, index) => {
        const validTypes = ["image", "video", "audio", "file", "pdf"];
        if (!validTypes.includes(block.type)) {
          return;
        }
        mapSignedUrlToFileUuid[block.props.url] = block.props.file_uuid;
      });
      localStorage.setItem(
        "mapSignedUrlToFileUuid",
        JSON.stringify(mapSignedUrlToFileUuid)
      );

      if (res.status === 200) {
        props.setModalDataSection(learningSection);
        setSelectedSectionUUID(learning_section_uuid || learningSection?.uuid);
        setIsLoading(false);
        return true;
      }
      return false;
    } catch (error) {
      throw Error(error);
    }
  };

  // Tracks whether the mount-time fetchSectionDetail call has resolved on
  // this CompleteTrack instance. Used to gate the mobile app_ready signal
  // below — UUID comparison alone isn't enough because modalDataSection is
  // parent-owned and survives CompleteTrack unmount/remount.
  const [mountFetchDone, setMountFetchDone] = React.useState(false);
  const initialSectionFetchUUIDRef = React.useRef(null);

  React.useEffect(() => {
    if (!props.showCompleteTrack || !initialSectionUUID) return;
    if (initialSectionFetchUUIDRef.current === initialSectionUUID) {
      return;
    }
    initialSectionFetchUUIDRef.current = initialSectionUUID;
    setMountFetchDone(false);
    fetchSectionDetail(
      true,
      initialSectionUUID,
      props.selectedEmployee ? props.selectedEmployee?.id : null
    )
      .then((didLoadSection) => {
        if (didLoadSection) setMountFetchDone(true);
      })
      // fetchSectionDetail rethrows on error; swallow here so we don't
      // surface an unhandled rejection. The native fallback timer
      // (ModalBottomCompleteTrack) will hide the overlay after 3s if
      // app_ready never fires.
      .catch(() => {});
    setSelectedSectionUUID(initialSectionUUID);
  }, [props.showCompleteTrack, initialSectionUUID]);

  // Mobile WebView hand-off: tell the native overlay
  // (ModalBottomCompleteTrack) it can hide once the first section's content
  // is loaded. Fired exactly once per CompleteTrack mount, gated on
  // embedded=1 so it's a no-op in normal browser use. Without this the
  // native overlay would either flash off too early (revealing the web
  // LoadSpinner mid-fetch with mismatched styling) or wait on a fallback
  // timer that doesn't track real readiness.
  //
  // Three gates, all required:
  //   1. mountFetchDone — proves the *current* mount's fetchSectionDetail
  //      resolved. modalDataSection is parent state and isn't reset on
  //      CompleteTrack unmount/remount, so reopening the same track (or
  //      another track sharing the first-section UUID) would otherwise
  //      satisfy the UUID check before the new fetch even starts.
  //   2. expectedSectionUuid match — guards against the rare case where
  //      mountFetchDone flips true but modalDataSection hasn't yet updated
  //      to the current mount's target section.
  //   3. appReadyFiredRef — one-shot per mount; subsequent section
  //      navigations inside the player must not re-fire.
  const appReadyFiredRef = React.useRef(false);
  React.useEffect(() => {
    if (!props.embedded) return;
    if (appReadyFiredRef.current) return;
    if (!mountFetchDone) return;
    const expectedSectionUuid =
      initialNextSectionUUIDRef.current || initialSectionUUID;
    if (!expectedSectionUuid) return;
    if (props.modalDataSection?.uuid !== expectedSectionUuid) return;
    if (typeof window === "undefined" || !window.ReactNativeWebView) return;
    appReadyFiredRef.current = true;
    try {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: "app_ready" })
      );
    } catch (_) {}
  }, [
    props.embedded,
    props.modalDataSection?.uuid,
    initialSectionUUID,
    mountFetchDone,
  ]);

  const completionSectionUUID =
    selectedSectionUUID || (mountFetchDone ? props.modalDataSection?.uuid : null);

  const quizzesData = props.quizzesData;
  const homeCompany = props.homeCompany;
  const post_learning_block_user_passed = props.post_learning_block_user_passed;
  const employeeID = props.selectedEmployee?.id;
  const userType = props.userType;
  const language = props.language

  // Memoized so the per-section <SectionPreview> editor gets a stable schema
  // for its lifetime. The interactive block render fns read the live section
  // via selectedSectionUUIDRef, so the ref (not the schema) carries section
  // identity; the remaining captured values are stable for the reader session.
  const schema = React.useMemo(() => buildTrainingSchema({
    quiz: (props) => (
      <Quiz
        {...props}
        quizzesData={quizzesData}
        homeCompany={homeCompany}
        post_learning_block_user_passed={post_learning_block_user_passed}
        employeeID={employeeID}
        setRefreshSection={setRefreshSection}
        userType={userType}
        language={language}
      />
    ),
    checkpoint: (props) => (
      <Checkpoint
        {...props}
        mode="complete"
        post_learning_block_user_passed={post_learning_block_user_passed}
        employeeID={employeeID}
        setRefreshSection={setRefreshSection}
        userType={userType}
        language={language}
      />
    ),
    pdf: (props) => <PDF {...props} />,
    signature: (props) => (
      <Signature
        {...props}
        homeCompany={homeCompany}
        mode="complete"
        employeeID={employeeID}
        language={language}
        sectionUUID={selectedSectionUUIDRef.current}
      />
    ),
    documentUpload: (props) => (
      <DocumentUpload
        {...props}
        homeCompany={homeCompany}
        mode="complete"
        employeeID={employeeID}
        language={language}
        sectionUUID={selectedSectionUUIDRef.current}
      />
    ),
    managerInput: (props) => (
      <ManagerInput
        {...props}
        homeCompany={homeCompany}
        mode="complete"
        employeeID={employeeID}
        userType={userType}
        language={language}
        sectionUUID={selectedSectionUUIDRef.current}
      />
    ),
    swipeDeck: (props) => (
      <SwipeDeck
        {...props}
        homeCompany={homeCompany}
        mode="complete"
        employeeID={employeeID}
        language={language}
        sectionUUID={selectedSectionUUIDRef.current}
        setRefreshSection={setRefreshSection}
      />
    ),
    form: (props) => (
      <Form
        {...props}
        homeCompany={homeCompany}
        mode="complete"
        employeeID={employeeID}
        userType={userType}
        language={language}
        sectionUUID={selectedSectionUUIDRef.current}
        setRefreshSection={setRefreshSection}
      />
    ),
    roleplay: (props) => (
      <Roleplay
        {...props}
        homeCompany={homeCompany}
        mode="complete"
        employeeID={employeeID}
        userType={userType}
        language={language}
        sectionUUID={selectedSectionUUIDRef.current}
        setRefreshSection={setRefreshSection}
      />
    ),
    videoEmbed: (props) => <VideoEmbed {...props} mode="complete" />,
  }), [
    quizzesData,
    homeCompany,
    post_learning_block_user_passed,
    employeeID,
    setRefreshSection,
    userType,
    language,
  ]);

  // The section content is now rendered by a self-contained, keyed
  // <SectionPreview> that mounts a fresh editor per section (see render). There
  // is no longer a shared reader editor for the nav handlers to clear, so
  // removeAllBlocks is a no-op kept only so existing callers don't change.
  const removeAllBlocks = () => {};

  // Helper function to find the next module with sections
  const findNextModuleWithSections = (startModuleIndex) => {
    if (!modules) return null;
    for (let i = startModuleIndex + 1; i < modules.length; i++) {
      if (modules[i]?.sections?.length > 0) {
        return i;
      }
    }
    return null;
  };

  // Helper function to find the previous module with sections
  const findPreviousModuleWithSections = (startModuleIndex) => {
    if (!modules) return null;
    for (let i = startModuleIndex - 1; i >= 0; i--) {
      if (modules[i]?.sections?.length > 0) {
        return i;
      }
    }
    return null;
  };

  const goToSection = (position) => {
    const uuid = modules?.[position.moduleIndex]?.sections?.[position.sectionIndex]?.uuid;
    if (!uuid) return;
    removeAllBlocks();
    fetchSectionDetail(
      true,
      uuid,
      props.selectedEmployee ? props.selectedEmployee?.id : null
    );
    setSelectedSectionUUID(uuid);
    setSelectedModuleIndex(position.moduleIndex);
    setSelectedSectionIndex(position.sectionIndex);
  };

  const currentSectionPosition = selectedSectionIndex ?? 0;
  const prevSectionPosition = (() => {
    if (currentSectionPosition > 0) {
      return { moduleIndex: selectedModuleIndex, sectionIndex: currentSectionPosition - 1 };
    }
    const prevModuleIndex = findPreviousModuleWithSections(selectedModuleIndex);
    if (prevModuleIndex === null) return null;
    return {
      moduleIndex: prevModuleIndex,
      sectionIndex: modules[prevModuleIndex].sections.length - 1,
    };
  })();

  // The learner taking their own training gets a single Continue button that
  // completes the section and advances in one tap. A manager viewing an
  // employee's track (selectedEmployee set) keeps the explicit Mark As
  // Completed + Next controls, so browsing never completes on their behalf.
  const isLearner = !props.selectedEmployee;

  // Sections completed in this session. The parent's track refetch updates
  // annotated_enrollment asynchronously, so without this a quick Back would
  // show the section as incomplete and re-post it (moving its completed_at).
  const [justCompletedUUIDs, setJustCompletedUUIDs] = React.useState(() => new Set());
  const [isContinuing, setIsContinuing] = React.useState(false);
  const [showFinished, setShowFinished] = React.useState(false);
  // Closing the player unmounts it; a completion still in flight must not
  // then navigate and write parent-owned section state for another track.
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const isSectionDone = (section, extraUUID = null) =>
    Boolean(section?.annotated_enrollment?.is_completed) ||
    justCompletedUUIDs.has(section?.uuid) ||
    (extraUUID !== null && section?.uuid === extraUUID);
  const isEverySectionDone = (extraUUID = null) =>
    Boolean(modules?.some((module) => module?.sections?.length > 0)) &&
    modules.every((module) =>
      (module?.sections || []).every((section) => isSectionDone(section, extraUUID))
    );

  const currentSectionDone =
    currentSectionIsCompleted || justCompletedUUIDs.has(completionSectionUUID);
  // A finished track is in review: Next just moves, nothing gets marked.
  const isReviewingTrack = isEverySectionDone();
  // Continue passes over tested-out sections. Back and the sidebar still open
  // them, and a finished track is walked in full.
  const nextSectionPosition = findNextSectionPosition(
    modules,
    selectedModuleIndex,
    currentSectionPosition,
    !isReviewingTrack
  );
  const isEndOfTrack = nextSectionPosition === null;

  const closePlayer = () => {
    if (props.setShowTopBar) {
      props.setShowTopBar(true);
    }
    props.setShowCompleteTrack(false);
    removeAllBlocks();
  };

  const handleContinue = async () => {
    if (isContinuing || !completionSectionUUID) return;
    const sectionUUID = completionSectionUUID;
    const wasReviewing = isReviewingTrack;
    if (!currentSectionDone) {
      setIsContinuing(true);
      const didComplete = await props.post_learning_section_completed(sectionUUID, null);
      if (!isMountedRef.current) return;
      setIsContinuing(false);
      if (!didComplete) return;
      setJustCompletedUUIDs((prev) => new Set(prev).add(sectionUUID));
      // The learner may have moved (Back, sidebar) while the request was in
      // flight; advancing from the stale position would yank them forward.
      if (selectedSectionUUIDRef.current !== sectionUUID) return;
    }
    if (!isEndOfTrack) {
      goToSection(nextSectionPosition);
    } else if (!wasReviewing && isEverySectionDone(sectionUUID)) {
      setShowFinished(true);
    } else {
      closePlayer();
    }
  };

  const continueLabel = (() => {
    if (isReviewingTrack) {
      return isEndOfTrack ? t("Done", "Listo", "完成") : t("Next", "Siguiente", "下一节");
    }
    if (isEndOfTrack) return t("Finish Track", "Finalizar Ruta", "完成课程");
    return currentSectionDone
      ? t("Next", "Siguiente", "下一节")
      : t("Continue", "Continuar", "继续");
  })();

  const totalSectionsCount = (modules || []).reduce(
    (count, module) => count + (module?.sections?.length || 0),
    0
  );

  // Navigation only: when a DIFFERENT section finishes loading (the loader path,
  // showLoading=true), start it at the top. Gated on the loaded UUID so it can't
  // fire for the current section. An in-place refresh (form/quiz/checkpoint
  // submit) no longer toggles isLoading, so this never runs on submit — keeping
  // the learner where they were instead of yanking them to the top.
  const lastScrolledSectionUUIDRef = React.useRef(null);
  React.useEffect(() => {
    if (isLoading || !(props.modalDataSection?.as_json?.length > 0)) return;
    const loadedUUID = props.modalDataSection?.uuid;
    if (!loadedUUID || loadedUUID === lastScrolledSectionUUIDRef.current) return;
    lastScrolledSectionUUIDRef.current = loadedUUID;
    preservedScrollRef.current = null; // a fresh section ignores any stale capture
    requestAnimationFrame(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    });
  }, [isLoading]);

  // In-place refresh (form/quiz/checkpoint submit): the section content updates
  // without unmounting, but re-seeding the blocks can still reset scroll. If a
  // refresh captured a position, restore it once the new content lays out.
  // Double rAF so the re-seeded blocks are measured before we scroll. Skipped on
  // navigation (the loader path clears preservedScrollRef first).
  React.useEffect(() => {
    if (isLoading || preservedScrollRef.current == null) return;
    const y = preservedScrollRef.current;
    preservedScrollRef.current = null;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (contentRef.current) {
          contentRef.current.scrollTop = y;
        }
      });
    });
  }, [props.modalDataSection]);

  // console.log("EDITOR DOCUMENT", editor.document)

  // Need to do
  return (
    <div className="w-full h-screen sm:h-dvh flex">
      {showMenu && (
        <div className="w-[25%] min-w-[225px] bg-white h-full overflow-auto sm:fixed sm:z-40 sm:shadow-lg sm:w-[70%]">
          <div className="px-4 pt-4 pb-6 border-b border-slate-200">
            <div
              className="flex items-center space-x-1.5 text-slate-500 hover:text-slate-600 cursor-pointer"
              onClick={() => setShowMenu(false)}
            >
              <div>
                <MenuIcon style={{ fontSize: "22px", marginBottom: "2px" }} />
              </div>
              <p className="text-slate-500 text-xs">{t("Hide", "Ocultar", "隐藏")}</p>
            </div>
            <h2 className="mt-8 text-2xl">{props.modalDataTrack?.name}</h2>
            <div className="mt-3 flex items-center space-x-2">
              <div className={`w-3 h-3 rounded bg-violet-700`}></div>
              <p className="text-slate-900 text-xs">
                {moduleCountLabel(modules?.length)}
              </p>
            </div>
            <div className="mt-6 flex items-center justify-between">
              <p className="text-slate-500 text-xxs">
                {percentCompleted}% {t("completed", "completado", "已完成")}
              </p>
              {/* <p className="text-slate-500 text-xxs">0 / {props.modalDataTrack?.modules?.length}</p> */}
            </div>
            <div className="w-full h-1.5 rounded-full bg-slate-200 mt-1.5 overflow-hidden">
              <div
                className="h-1.5 bg-emerald-600 rounded-full"
                style={{ width: `${percentCompleted}%` }}
              ></div>
            </div>
          </div>
          <div>
            {modules?.map((module, index) => {
              return (
                <div key={index}>
                  <TrackSidebarModuleDropdown
                    module={module}
                    index={index}
                    selectedModuleIndex={selectedModuleIndex}
                    setSelectedModuleIndex={setSelectedModuleIndex}
                    fetchSectionDetail={fetchSectionDetail}
                    removeAllBlocks={removeAllBlocks}
                    selectedSectionIndex={selectedSectionIndex}
                    setSelectedSectionIndex={setSelectedSectionIndex}
                    setSelectedSectionUUID={setSelectedSectionUUID}
                    modalDataSection={props.modalDataSection}
                    isLoading={isLoading}
                    userType={props.userType}
                    nextModuleIndex={props.currentTrack?.next_module_index}
                    nextSectionIndex={props.currentTrack?.next_section_index}
                    setIsCurrentSectionCompleted={setIsCurrentSectionCompleted}
                    selectedEmployee={props.selectedEmployee}
                    language={props.language}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
      {showMenu && <div className="w-2 bg-slate-100 shadow-inner"></div>}
      <div
        className={`flex-1 sm:w-full h-screen sm:fixed sm:h-full`}
        onClick={() => {
          if (isSmallScreen && showMenu) {
            setShowMenu(false);
          }
        }}
      >
        <div className="w-full h-full flex flex-col">
          <div>
            <div className="w-full bg-white flex justify-center shadow relative z-50">
              <div className="px-12 py-2.5 text-center">
                {props.employeeName && (
                  <p className="text-slate-500 text-xs font-medium">
                    {formatName(props.employeeName)}
                  </p>
                )}
                <p className="text-slate-400 text-xs font-semibold uppercase">
                  {t("Module", "Módulo", "模块")} {selectedModuleIndex + 1}{" "}
                  {selectedSectionIndex !== null &&
                    `- ${t("Section", "Sección", "章节")} ${selectedSectionIndex + 1}`}
                </p>
                {selectedSectionTitle && (
                  <h2 className="mt-0.5 line-clamp-2 text-lg font-semibold text-slate-900 sm:text-base">
                    {selectedSectionTitle}
                  </h2>
                )}
              </div>
              {!showMenu && (
                <div className="absolute left-2 top-2">
                  <div
                    className="flex items-center space-x-1 text-slate-500 hover:text-slate-600 cursor-pointer"
                    onClick={() => setShowMenu(true)}
                  >
                    <div>
                      <MenuIcon
                        style={{ fontSize: "22px", marginBottom: "2px" }}
                      />
                    </div>
                  </div>
                </div>
              )}
              {!props.embedded && (
                <div
                  className="absolute right-2 top-2 text-gray-500 cursor-pointer z-40"
                  onClick={() => {
                    if (props.setShowTopBar) {
                      props.setShowTopBar(true);
                    }
                    props.setShowCompleteTrack(false);
                    removeAllBlocks();
                  }}
                >
                  <CloseIcon />
                </div>
              )}
            </div>
            <div className="h-1.5 bg-slate-100 shadow-inner"></div>
          </div>
          <div ref={contentRef} className="w-full flex-1 relative pt-5 overflow-auto bg-white shadow">
            {/* key={modalDataSection.uuid} mounts a fresh editor per section,
                seeded via initialContent — no shared reader editor whose
                document desyncs across remove/replace cycles (the old bug that
                threw "Block with ID … not found" / blanked the pane). */}
            {!isLoading && (
              <div className="bn-reader-mobile-tight">
                <SectionPreview
                  key={props.modalDataSection?.uuid}
                  schema={schema}
                  asJson={props.modalDataSection?.as_json}
                />
              </div>
            )}
            {isLoading && (
              <div
                className="bg-white"
                style={{ position: "absolute", inset: 0 }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <LoadSpinner />
                </div>
              </div>
            )}
          </div>
          <div className="w-full flex flex-col">
            <div className="w-full h-1.5 bg-slate-100 shadow-inner"></div>
            {isLearner ? (
              <div className="w-full bg-white flex items-center gap-x-3 px-6 py-3 flex-1 shadow">
                {prevSectionPosition && (
                  <button
                    type="button"
                    className="py-2 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100 flex items-center gap-x-1"
                    onClick={() => goToSection(prevSectionPosition)}
                  >
                    <ChevronLeftIcon />
                    <p>{t("Back", "Atrás", "返回")}</p>
                  </button>
                )}
                <button
                  type="button"
                  disabled={!completionSectionUUID || isLoading || isContinuing}
                  className="ml-auto py-2 px-6 sm:flex-1 rounded-lg text-sm font-medium text-white bg-violet-700 hover:bg-violet-800 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed flex items-center justify-center gap-x-1.5"
                  onClick={handleContinue}
                >
                  <p>{continueLabel}</p>
                  {!isEndOfTrack && <ChevronRightIcon />}
                </button>
              </div>
            ) : (
            <div className="w-full bg-white flex items-center justify-between px-6 py-3 flex-1 shadow">
              {completionSectionUUID ? (
                isCurrentSectionCompleted ? (
                  <div className="py-2 px-4 bg-slate-200 rounded-lg text-sm text-slate-400 h-fit flex items-center justify-center gap-x-3">
                    <p className="md:hidden">{t("Mark As Completed", "Marcar Como Completado", "标记为已完成")}</p>
                    <p className="hidden md:block">{t("Complete", "Completar", "完成")}</p>
                    <div>
                      <TaskAltIcon />
                    </div>
                  </div>
                ) : (
                  <div
                    className="py-2 px-4 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-sm text-white cursor-pointer h-fit flex items-center justify-center gap-x-3"
                    onClick={() =>
                      props.post_learning_section_completed(
                        completionSectionUUID,
                        props.selectedEmployee
                          ? props.selectedEmployee.id
                          : null
                      )
                    }
                  >
                    <p className="md:hidden">{t("Mark As Completed", "Marcar Como Completado", "标记为已完成")}</p>
                    <p className="hidden md:block">{t("Complete", "Completar", "完成")}</p>
                    <div>
                      <TaskAltIcon />
                    </div>
                  </div>
                )
              ) : (
                <div></div>
              )}
              <div className="flex items-center space-x-4">
                {firstSectionInTrack ? (
                  <div></div>
                ) : firstSectionInModule ? (
                  (() => {
                    const prevModuleIndex = findPreviousModuleWithSections(selectedModuleIndex);
                    if (prevModuleIndex === null) {
                      return <div></div>;
                    }
                    return (
                      <div
                        className="py-2 px-4 bg-violet-700 hover:bg-violet-800 rounded-lg text-sm text-white flex items-center justify-center gap-x-1.5 cursor-pointer"
                        onClick={() => {
                          removeAllBlocks();
                          const lastSectionIndex = modules?.[prevModuleIndex].sections?.length - 1;
                          fetchSectionDetail(
                            true,
                            modules?.[prevModuleIndex]?.sections?.[lastSectionIndex]?.uuid,
                            props.selectedEmployee
                              ? props.selectedEmployee?.id
                              : null
                          );
                          setSelectedSectionUUID(
                            modules?.[prevModuleIndex]?.sections?.[lastSectionIndex]?.uuid
                          );
                          setSelectedSectionIndex(lastSectionIndex);
                          setSelectedModuleIndex(prevModuleIndex);
                        }}
                      >
                        <div>
                          <ChevronLeftIcon />
                        </div>
                        <p className="md:hidden">{t("Last Section", "Última Sección", "上一节")}</p>
                      </div>
                    );
                  })()
                ) : (
                  <div
                    className="py-2 px-4 bg-violet-700 hover:bg-violet-800 rounded-lg text-sm text-white flex items-center justify-center gap-x-1.5 cursor-pointer"
                    onClick={() => {
                      removeAllBlocks();
                      fetchSectionDetail(
                        true,
                        modules?.[selectedModuleIndex]?.sections?.[
                          selectedSectionIndex - 1
                        ]?.uuid,
                        props.selectedEmployee
                          ? props.selectedEmployee?.id
                          : null
                      );
                      setSelectedSectionUUID(
                        modules?.[selectedModuleIndex]?.sections?.[
                          selectedSectionIndex - 1
                        ]?.uuid
                      );
                      setSelectedSectionIndex(selectedSectionIndex - 1);
                    }}
                  >
                    <div>
                      <ChevronLeftIcon />
                    </div>
                    <p className="md:hidden">{t("Last Section", "Última Sección", "上一节")}</p>
                  </div>
                )}
                {lastSectionInTrack ? (
                  employeeBlockedFromAdvancing ? (
                    <div className="py-2 px-4 bg-slate-200 rounded-lg text-sm text-slate-400 flex items-center justify-center gap-x-1.5">
                      <p className="md:hidden">{t("Finish Track", "Finalizar Ruta", "完成课程")}</p>
                      <div>
                        <ChevronRightIcon />
                      </div>
                    </div>
                  ) : (
                    <div
                      className="py-2 px-4 bg-violet-700 hover:bg-violet-800 rounded-lg text-sm text-white flex items-center justify-center gap-x-1.5 cursor-pointer"
                      onClick={() => {
                        props.setShowCompleteTrack(false);
                        removeAllBlocks();
                      }}
                    >
                      <p className="md:hidden">{t("Finish Track", "Finalizar Ruta", "完成课程")}</p>
                      <div>
                        <ChevronRightIcon />
                      </div>
                    </div>
                  )
                ) : lastSectionInModule ? (
                  (() => {
                    const nextModuleIndex = findNextModuleWithSections(selectedModuleIndex);

                    if (employeeBlockedFromAdvancing) {
                      return (
                        <div className="py-2 px-4 bg-slate-200 rounded-lg text-sm text-slate-400 flex items-center justify-center gap-x-1.5">
                          <p className="md:hidden">{t("Next Section", "Siguiente Sección", "下一节")}</p>
                          <div>
                            <ChevronRightIcon />
                          </div>
                        </div>
                      );
                    }

                    if (nextModuleIndex === null) {
                      return (
                        <div className="py-2 px-4 bg-slate-200 rounded-lg text-sm text-slate-400 flex items-center justify-center gap-x-1.5">
                          <p className="md:hidden">{t("No more sections", "No hay más secciones", "没有更多章节")}</p>
                          <div>
                            <ChevronRightIcon />
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        className="py-2 px-4 bg-violet-700 hover:bg-violet-800 rounded-lg text-sm text-white flex items-center justify-center gap-x-1.5 cursor-pointer"
                        onClick={() => {
                          removeAllBlocks();
                          fetchSectionDetail(
                            true,
                            modules?.[nextModuleIndex]?.sections?.[0]?.uuid,
                            props.selectedEmployee
                              ? props.selectedEmployee?.id
                              : null
                          );
                          setSelectedSectionUUID(
                            modules?.[nextModuleIndex]?.sections?.[0]?.uuid
                          );
                          setSelectedModuleIndex(nextModuleIndex);
                          setSelectedSectionIndex(0);
                        }}
                      >
                        <p className="md:hidden">{t("Next Section", "Siguiente Sección", "下一节")}</p>
                        <div>
                          <ChevronRightIcon />
                        </div>
                      </div>
                    );
                  })()
                ) : employeeBlockedFromAdvancing ? (
                  <div className="py-2 px-4 bg-slate-200 rounded-lg text-sm text-slate-400 flex items-center justify-center gap-x-1.5">
                    <p className="md:hidden">{t("Next Section", "Siguiente Sección", "下一节")}</p>
                    <div>
                      <ChevronRightIcon />
                    </div>
                  </div>
                ) : (
                  <div
                    className="py-2 px-4 bg-violet-700 hover:bg-violet-800 rounded-lg text-sm text-white flex items-center justify-center gap-x-1.5 cursor-pointer"
                    onClick={() => {
                      removeAllBlocks();
                      fetchSectionDetail(
                        true,
                        modules?.[selectedModuleIndex]?.sections?.[
                          selectedSectionIndex + 1
                        ]?.uuid,
                        props.selectedEmployee
                          ? props.selectedEmployee?.id
                          : null
                      );
                      setSelectedSectionUUID(
                        modules?.[selectedModuleIndex]?.sections?.[
                          selectedSectionIndex + 1
                        ]?.uuid
                      );
                      setSelectedSectionIndex(selectedSectionIndex + 1);
                    }}
                  >
                    <p className="md:hidden">{t("Next Section", "Siguiente Sección", "下一节")}</p>
                    <div>
                      <ChevronRightIcon />
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
      {showFinished && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center px-8 text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
            <TaskAltIcon style={{ fontSize: "44px" }} />
          </div>
          <h2 className="mt-6 text-2xl font-semibold text-slate-900 sm:text-xl">
            {t("You finished", "Terminaste", "你已完成")} {props.modalDataTrack?.name}
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            {totalSectionsCount} / {totalSectionsCount}{" "}
            {t("sections complete", "secciones completadas", "个章节已完成")}
          </p>
          <div className="mt-8 w-full max-w-xs flex flex-col gap-y-2">
            <button
              type="button"
              className="py-2.5 px-4 bg-violet-700 hover:bg-violet-800 rounded-lg text-sm font-medium text-white"
              onClick={closePlayer}
            >
              {t("Back to my training", "Volver a mi capacitación", "返回我的培训")}
            </button>
            <button
              type="button"
              className="py-2.5 px-4 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
              onClick={() => {
                setShowFinished(false);
                setShowMenu(true);
              }}
            >
              {t("Review sections", "Repasar secciones", "复习章节")}
            </button>
          </div>
        </div>
      )}
      <BadgeEarnedModal
        open={certEarnedOpen}
        onClose={() => setCertEarnedOpen(false)}
        badgeName={props.modalDataTrack?.grants_badge_name}
        trackName={props.modalDataTrack?.name}
        language={props.language || "english"}
      />
    </div>
  );
}

export default CompleteTrack;
